import { useState, useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Sparkles, Wand2, Check, X, Loader2, Download, Store, Package, Search, Library, Save, Eye, EyeOff } from 'lucide-react';
import { Button, Input } from '../components/ui';
import { useShops } from '../contexts/ShopContext';
import { useAuth } from '../contexts/AuthContext';
import { generateImage, uploadImageToShopify, ShopifyProduct, getCachedProducts } from '../services/api';
import { getPrompts, createPrompt, Prompt } from '../services/promptService';
import { getNegativePrompt } from '../services/negativePromptService';
import { buildProductContextText, buildFullPrompt } from '../services/productContextService';
import { generateSeoFilename, generateProductImageSeoMetadata } from '../services/seoService';
import { collection, addDoc, Timestamp, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import './GeneratorPage.css';

export function GeneratorPage() {
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { userProfile } = useAuth();
    const { shops, selectedShop, selectShop } = useShops();
    const [prompt, setPrompt] = useState('');
    const [selectedModel, setSelectedModel] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [generatedImage, setGeneratedImage] = useState<{ data: string; mimeType: string } | null>(null);
    const [error, setError] = useState('');

    // Product selection
    const [showProductModal, setShowProductModal] = useState(false);
    const [products, setProducts] = useState<ShopifyProduct[]>([]);
    const [isLoadingProducts, setIsLoadingProducts] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedProduct, setSelectedProduct] = useState<ShopifyProduct | null>(null);
    const [uploadSuccess, setUploadSuccess] = useState(false);

    // Source product (from URL parameter - when coming from products page)
    const [sourceProduct, setSourceProduct] = useState<ShopifyProduct | null>(null);
    const [isLoadingSourceProduct, setIsLoadingSourceProduct] = useState(false);

    // SEO options for upload
    const [seoFilename, setSeoFilename] = useState('');
    const [altText, setAltText] = useState('');

    // Product context enrichment option
    const [useProductContext, setUseProductContext] = useState(true);

    // Negative prompt option
    const [negativePrompt, setNegativePrompt] = useState('');
    const [useNegativePrompt, setUseNegativePrompt] = useState(true);
    const [isLoadingNegativePrompt, setIsLoadingNegativePrompt] = useState(false);

    // Prompt library
    const [showLibraryModal, setShowLibraryModal] = useState(false);
    const [prompts, setPrompts] = useState<Prompt[]>([]);
    const [isLoadingPrompts, setIsLoadingPrompts] = useState(false);
    const [promptSearchQuery, setPromptSearchQuery] = useState('');

    // Save prompt
    const [showSaveModal, setShowSaveModal] = useState(false);
    const [savePromptTitle, setSavePromptTitle] = useState('');
    const [savePromptType, setSavePromptType] = useState<'public' | 'private'>('private');
    const [isSavingPrompt, setIsSavingPrompt] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);

    // Prompt sent to AI (for transparency)
    const [usedPrompt, setUsedPrompt] = useState<string | null>(null);
    const [showPromptInfo, setShowPromptInfo] = useState(false);

    // Available AI models (loaded from Firestore)
    interface AIModel {
        id: string;
        name: string;
        provider: string;
        type: 'text' | 'image';
    }
    const [availableModels, setAvailableModels] = useState<AIModel[]>([]);
    const [isLoadingModels, setIsLoadingModels] = useState(true);

    // Load selected models from Firestore (configured in admin settings)
    useEffect(() => {
        const loadModels = async () => {
            try {
                // Load from selected_models document (admin configured)
                const selectedModelsDoc = await getDoc(doc(db, 'settings', 'selected_models'));
                if (selectedModelsDoc.exists()) {
                    const data = selectedModelsDoc.data();
                    const allModels: AIModel[] = [];

                    // Load Gemini models - prefer ModelsInfo, fallback to IDs
                    if (data.geminiModelsInfo && Array.isArray(data.geminiModelsInfo) && data.geminiModelsInfo.length > 0) {
                        for (const model of data.geminiModelsInfo) {
                            allModels.push({
                                id: model.id,
                                name: model.displayName || model.id,
                                provider: 'google',
                                type: model.type || (model.id.includes('image') ? 'image' : 'text'),
                            });
                        }
                    } else if (data.gemini && Array.isArray(data.gemini) && data.gemini.length > 0) {
                        // Fallback: use IDs directly when ModelsInfo is empty
                        for (const modelId of data.gemini) {
                            allModels.push({
                                id: modelId,
                                name: modelId,
                                provider: 'google',
                                type: modelId.includes('image') ? 'image' : 'text',
                            });
                        }
                    }

                    // Load OpenAI models - prefer ModelsInfo, fallback to IDs
                    if (data.openaiModelsInfo && Array.isArray(data.openaiModelsInfo) && data.openaiModelsInfo.length > 0) {
                        for (const model of data.openaiModelsInfo) {
                            allModels.push({
                                id: model.id,
                                name: model.displayName || model.id,
                                provider: 'openai',
                                type: model.type || (model.id.includes('dall-e') ? 'image' : 'text'),
                            });
                        }
                    } else if (data.openai && Array.isArray(data.openai) && data.openai.length > 0) {
                        // Fallback: use IDs directly when ModelsInfo is empty
                        for (const modelId of data.openai) {
                            allModels.push({
                                id: modelId,
                                name: modelId,
                                provider: 'openai',
                                type: modelId.includes('dall-e') || modelId.includes('gpt-image') ? 'image' : 'text',
                            });
                        }
                    }

                    if (allModels.length > 0) {
                        setAvailableModels(allModels);

                        // Try to restore last used model from localStorage
                        const savedModelId = localStorage.getItem('lastSelectedModel');
                        const savedModelAvailable = savedModelId && allModels.some(m => m.id === savedModelId);

                        if (savedModelAvailable) {
                            setSelectedModel(savedModelId);
                        } else {
                            // Default to first image model, or first model
                            const imageModel = allModels.find(m => m.type === 'image');
                            setSelectedModel(imageModel?.id || allModels[0].id);
                        }
                    }
                }
            } catch (err) {
                console.error('Error loading models:', err);
            } finally {
                setIsLoadingModels(false);
            }
        };
        loadModels();
    }, []);

    // Save selected model to localStorage when it changes (only after models are loaded)
    useEffect(() => {
        if (selectedModel && !isLoadingModels) {
            localStorage.setItem('lastSelectedModel', selectedModel);
        }
    }, [selectedModel, isLoadingModels]);

    // Set prompt from navigation state (from Prompt Library)
    useEffect(() => {
        const state = location.state as { promptContent?: string } | null;
        if (state?.promptContent) {
            setPrompt(state.promptContent);
            // Clear the state to prevent re-setting on navigation
            window.history.replaceState({}, document.title);
        }
    }, [location.state]);

    // Load source product from URL parameter (when coming from products page)
    useEffect(() => {
        const productId = searchParams.get('productId');

        // Debug logging
        console.log('[Generator] Product load check:', {
            productId,
            hasSelectedShop: !!selectedShop,
            selectedShopId: selectedShop?.id,
            hasSourceProduct: !!sourceProduct,
            sourceProductId: sourceProduct?.id,
        });

        if (!productId) {
            console.log('[Generator] No productId in URL, skipping product load');
            return;
        }

        if (sourceProduct) {
            console.log('[Generator] Source product already loaded:', sourceProduct.title);
            return;
        }

        if (!selectedShop) {
            console.log('[Generator] Waiting for selectedShop to be available...');
            return;
        }

        const loadSourceProduct = async () => {
            console.log('[Generator] Loading source product for ID:', productId, 'from shop:', selectedShop.id);
            setIsLoadingSourceProduct(true);
            try {
                // Fetch products from cache and find the one matching the ID
                const result = await getCachedProducts(selectedShop.id);
                console.log('[Generator] Fetched', result.products.length, 'products from cache');
                const product = result.products.find(p => String(p.id) === productId);
                if (product) {
                    setSourceProduct(product);
                    console.log('[Generator] ✅ Source product loaded from cache:', product.title);
                } else {
                    console.error('[Generator] ❌ Product not found in cache with ID:', productId);
                }
            } catch (err) {
                console.error('[Generator] Error loading source product:', err);
            } finally {
                setIsLoadingSourceProduct(false);
            }
        };

        loadSourceProduct();
    }, [searchParams, selectedShop, sourceProduct]);

    // Load negative prompt when shop changes
    useEffect(() => {
        if (!selectedShop?.id) {
            setNegativePrompt('');
            return;
        }

        const loadNegativePromptForShop = async () => {
            setIsLoadingNegativePrompt(true);
            try {
                const np = await getNegativePrompt(selectedShop.id);
                setNegativePrompt(np?.content || '');
            } catch (err) {
                console.error('[Generator] Error loading negative prompt:', err);
                setNegativePrompt('');
            } finally {
                setIsLoadingNegativePrompt(false);
            }
        };

        loadNegativePromptForShop();
    }, [selectedShop?.id]);

    // Log generation to Firestore
    const logGeneration = async (status: 'success' | 'rejected' | 'uploaded', productId?: string) => {
        if (!userProfile?.uid) return;

        try {
            await addDoc(collection(db, 'logs'), {
                userId: userProfile.uid,
                shopId: selectedShop?.id || null,
                productId: productId || null,
                model: selectedModel,
                promptUsed: prompt.trim(),
                status,
                createdAt: Timestamp.now(),
            });
        } catch (err) {
            console.error('Error logging generation:', err);
        }
    };

    // Convert base64 image to WebP format for better optimization
    const convertToWebP = (base64Data: string, mimeType: string): Promise<string> => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    reject(new Error('Could not get canvas context'));
                    return;
                }
                ctx.drawImage(img, 0, 0);
                // Convert to WebP with 90% quality
                const webpDataUrl = canvas.toDataURL('image/webp', 0.9);
                // Extract base64 data without the data:image/webp;base64, prefix
                const webpBase64 = webpDataUrl.split(',')[1];
                resolve(webpBase64);
            };
            img.onerror = () => reject(new Error('Failed to load image for conversion'));
            img.src = `data:${mimeType};base64,${base64Data}`;
        });
    };
    // Note: SEO generation is now handled by seoService.ts (shared between manual and automation)

    // Update SEO suggestions when image is generated (only for fallback case without source product)
    useEffect(() => {
        if (!generatedImage) return;

        // Only set from prompt if no source product (source product case is handled in handleGenerate)
        if (!sourceProduct) {
            const promptPart = generateSeoFilename(prompt.substring(0, 40));
            setSeoFilename(promptPart || 'product-image');
            setAltText(prompt.substring(0, 100));
        }
    }, [generatedImage]);

    const handleGenerate = async () => {
        if (!prompt.trim()) return;

        setIsGenerating(true);
        setGeneratedImage(null);
        setError('');
        setUploadSuccess(false);
        setUsedPrompt(null);
        setShowPromptInfo(false);

        // Pre-calculate SEO filename and alt text using shared service (SAME AS AUTOMATION)
        if (sourceProduct) {
            const existingImageCount = sourceProduct.images?.length || 0;
            const seoMetadata = generateProductImageSeoMetadata(sourceProduct.title, existingImageCount);
            setSeoFilename(seoMetadata.filename.replace('.webp', '')); // Remove extension for display
            setAltText(seoMetadata.altText);
        } else {
            // Fallback to prompt-based naming
            const promptPart = generateSeoFilename(prompt.substring(0, 40));
            setSeoFilename(promptPart || 'product-image');
            setAltText(prompt.substring(0, 100));
        }

        try {
            // Build the prompt with optional product context using shared service
            let promptToSend = prompt.trim();

            if (useProductContext && sourceProduct) {
                // Use shared product context builder - same as automation
                const productContext = buildProductContextText(sourceProduct, {
                    includeImages: true,
                    maxDescriptionLength: 300,
                    maxImages: 3,
                });
                promptToSend = productContext + prompt.trim();
            }

            // Add negative prompt if enabled using shared service
            promptToSend = buildFullPrompt(
                useProductContext ? promptToSend : prompt.trim(),
                null, // context already included in promptToSend
                useNegativePrompt ? negativePrompt : undefined
            );

            const result = await generateImage({
                prompt: promptToSend,
                model: selectedModel,
            });

            setGeneratedImage(result.image);
            setUsedPrompt(promptToSend);
            await logGeneration('success');
        } catch (err: any) {
            setError(err.message || 'Erreur lors de la génération');
        } finally {
            setIsGenerating(false);
        }
    };

    // Load products when modal opens
    const loadProducts = async () => {
        if (!selectedShop) return;

        setIsLoadingProducts(true);
        try {
            const result = await getCachedProducts(selectedShop.id);
            setProducts(result.products);
        } catch (err: any) {
            console.error('Error loading products:', err);
            setError('Erreur lors du chargement des produits');
        } finally {
            setIsLoadingProducts(false);
        }
    };

    // Load prompts for library modal
    const loadPrompts = async () => {
        if (!userProfile?.uid) return;

        setIsLoadingPrompts(true);
        try {
            const data = await getPrompts(userProfile.uid);
            setPrompts(data);
        } catch (err: any) {
            console.error('Error loading prompts:', err);
            setError('Erreur lors du chargement des prompts');
        } finally {
            setIsLoadingPrompts(false);
        }
    };

    // Open library modal
    const handleOpenLibrary = () => {
        setShowLibraryModal(true);
        setPromptSearchQuery('');
        loadPrompts();
    };

    // Select prompt from library
    const handleSelectPrompt = (selectedPrompt: Prompt) => {
        setPrompt(selectedPrompt.content);
        // Restore useProductContext setting from saved prompt
        if (selectedPrompt.useProductContext !== undefined) {
            setUseProductContext(selectedPrompt.useProductContext);
        }
        setShowLibraryModal(false);
    };

    // Open save prompt modal
    const handleOpenSaveModal = () => {
        setSavePromptTitle('');
        setSavePromptType('private');
        setSaveSuccess(false);
        setShowSaveModal(true);
    };

    // Save prompt to library
    const handleSavePrompt = async () => {
        if (!userProfile?.uid || !prompt.trim() || !savePromptTitle.trim()) return;

        setIsSavingPrompt(true);
        try {
            await createPrompt(
                {
                    title: savePromptTitle.trim(),
                    content: prompt.trim(),
                    type: savePromptType,
                    useProductContext: useProductContext,
                },
                userProfile.uid,
                userProfile.displayName || 'Utilisateur'
            );
            setSaveSuccess(true);
            setTimeout(() => {
                setShowSaveModal(false);
                setSaveSuccess(false);
            }, 1500);
        } catch (err: any) {
            console.error('Error saving prompt:', err);
            setError('Erreur lors de la sauvegarde du prompt');
        } finally {
            setIsSavingPrompt(false);
        }
    };

    // Filter prompts by search query
    const filteredPrompts = prompts.filter(p =>
        p.title.toLowerCase().includes(promptSearchQuery.toLowerCase()) ||
        p.content.toLowerCase().includes(promptSearchQuery.toLowerCase())
    );

    const handleAccept = async () => {
        if (!generatedImage) {
            setError('Aucune image à uploader');
            return;
        }

        if (!selectedShop) {
            setError('Veuillez connecter une boutique Shopify');
            return;
        }

        // If we have a source product (from products page), upload directly without modal
        if (sourceProduct) {
            setSelectedProduct(sourceProduct);
            // Start upload directly
            setIsUploading(true);
            setError('');
            try {
                // Convert to WebP format for better optimization
                const webpData = await convertToWebP(generatedImage.data, generatedImage.mimeType);
                const finalFilename = seoFilename ? `${seoFilename}.webp` : `product-image-${Date.now()}.webp`;
                await uploadImageToShopify({
                    shopId: selectedShop.id,
                    productId: String(sourceProduct.id),
                    imageBase64: webpData,
                    filename: finalFilename,
                    altText: altText || undefined,
                });

                await logGeneration('uploaded', String(sourceProduct.id));
                setUploadSuccess(true);
                setGeneratedImage(null);
                setPrompt('');
            } catch (err: any) {
                setError(err.message || 'Erreur lors de l\'upload');
            } finally {
                setIsUploading(false);
            }
            return;
        }

        // Otherwise, open product selection modal
        setShowProductModal(true);
        setSelectedProduct(null);
        loadProducts();
    };

    const handleUploadToProduct = async () => {
        if (!selectedProduct || !generatedImage || !selectedShop) return;

        setIsUploading(true);
        setError('');

        try {
            // Convert to WebP format for better optimization
            const webpData = await convertToWebP(generatedImage.data, generatedImage.mimeType);
            const finalFilename = seoFilename ? `${seoFilename}.webp` : `product-image-${Date.now()}.webp`;
            await uploadImageToShopify({
                shopId: selectedShop.id,
                productId: String(selectedProduct.id),
                imageBase64: webpData,
                filename: finalFilename,
                altText: altText || undefined,
            });

            await logGeneration('uploaded', String(selectedProduct.id));
            setShowProductModal(false);
            setUploadSuccess(true);
            setGeneratedImage(null);
            setPrompt('');
            setSelectedProduct(null);
        } catch (err: any) {
            setError(err.message || 'Erreur lors de l\'upload');
        } finally {
            setIsUploading(false);
        }
    };

    const handleReject = async () => {
        await logGeneration('rejected');
        setGeneratedImage(null);
        setUploadSuccess(false);
    };

    const handleDownload = () => {
        if (!generatedImage) return;

        const link = document.createElement('a');
        link.href = `data:${generatedImage.mimeType};base64,${generatedImage.data}`;
        link.download = `ai-generated-${Date.now()}.png`;
        link.click();
    };

    // Filter products by search query
    const filteredProducts = products.filter(product =>
        product.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.vendor?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="generator-page animate-fadeIn">
            <header className="page-header">
                <div>
                    <h1 className="page-title">Générer une image</h1>
                    <p className="page-subtitle">Créez des visuels produits avec l'IA</p>
                </div>

                {/* Shop Selector */}
                {shops.length > 0 && (
                    <div className="shop-selector">
                        <Store size={18} />
                        <select
                            value={selectedShop?.id || ''}
                            onChange={(e) => {
                                const shop = shops.find(s => s.id === e.target.value);
                                if (shop) selectShop(shop);
                            }}
                            className="shop-select"
                        >
                            {shops.map(shop => (
                                <option key={shop.id} value={shop.id}>{shop.name}</option>
                            ))}
                        </select>
                    </div>
                )}
            </header>

            {uploadSuccess && (
                <div className="success-message">
                    <Check size={20} />
                    Image uploadée avec succès sur Shopify !
                </div>
            )}

            <div className="generator-container">
                <div className="generator-form glass">
                    <div className="form-section">
                        <label className="form-label">Modèle IA</label>
                        {isLoadingModels ? (
                            <div className="models-loading-inline">
                                <Loader2 size={16} className="animate-spin" />
                                <span>Chargement des modèles...</span>
                            </div>
                        ) : availableModels.length === 0 ? (
                            <div className="models-empty-warning">
                                <span>⚠️ Aucun modèle configuré.</span>
                                <span>Configurez les modèles IA dans les <a href="/admin/settings">Paramètres Admin</a>.</span>
                            </div>
                        ) : (
                            <div className="generator-models-list">
                                {availableModels.map((model) => (
                                    <div
                                        key={model.id}
                                        className={`generator-model-item ${selectedModel === model.id ? 'selected' : ''}`}
                                        onClick={() => setSelectedModel(model.id)}
                                    >
                                        <div className="generator-model-info">
                                            <div className="generator-model-name-row">
                                                <span className="generator-model-name">{model.id}</span>
                                                <span className={`model-type-badge ${model.type}`}>
                                                    {model.type === 'image' ? 'Image' : 'Texte'}
                                                </span>
                                            </div>
                                        </div>
                                        {selectedModel === model.id && (
                                            <div className="generator-model-selected">
                                                <Check size={16} />
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="form-section">
                        <div className="form-label-row">
                            <label className="form-label">Prompt</label>
                            {prompt.trim() && (
                                <button
                                    type="button"
                                    className="save-prompt-btn"
                                    onClick={handleOpenSaveModal}
                                    title="Sauvegarder le prompt"
                                >
                                    <Save size={16} />
                                    Sauvegarder
                                </button>
                            )}
                        </div>
                        <textarea
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            placeholder="Décrivez l'image que vous souhaitez générer...&#10;&#10;Ex: A professional product photo of a red leather handbag on a white background, studio lighting, high quality"
                            className="prompt-textarea"
                            rows={5}
                        />
                    </div>

                    <div className="form-section">
                        <label className="form-label">Ou choisissez un prompt enregistré</label>
                        <Button
                            variant="secondary"
                            size="sm"
                            leftIcon={<Library size={16} />}
                            onClick={handleOpenLibrary}
                        >
                            Ouvrir la bibliothèque
                        </Button>
                    </div>

                    {error && <div className="error-message">{error}</div>}

                    {/* Product context option - show immediately when coming from products page */}
                    {(sourceProduct || searchParams.get('productId')) && (
                        <div className="context-option">
                            <label className="context-checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={useProductContext}
                                    onChange={(e) => setUseProductContext(e.target.checked)}
                                    className="context-checkbox"
                                    disabled={isLoadingSourceProduct}
                                />
                                <span className="context-checkbox-text">
                                    Enrichir avec le contexte du produit
                                </span>
                                {isLoadingSourceProduct ? (
                                    <span className="context-checkbox-hint context-loading">
                                        <Loader2 size={12} className="animate-spin" /> Chargement du produit...
                                    </span>
                                ) : (
                                    <span className="context-checkbox-hint">
                                        (titre, description, tags, images existantes)
                                    </span>
                                )}
                            </label>
                        </div>
                    )}

                    {/* Negative prompt option - only show when shop is selected and has negative prompt */}
                    {selectedShop && negativePrompt && (
                        <div className="context-option negative-prompt-option">
                            <label className="context-checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={useNegativePrompt}
                                    onChange={(e) => setUseNegativePrompt(e.target.checked)}
                                    className="context-checkbox"
                                    disabled={isLoadingNegativePrompt}
                                />
                                <span className="context-checkbox-text">
                                    Appliquer le prompt négatif
                                </span>
                                {isLoadingNegativePrompt ? (
                                    <span className="context-checkbox-hint context-loading">
                                        <Loader2 size={12} className="animate-spin" /> Chargement...
                                    </span>
                                ) : (
                                    <span className="context-checkbox-hint">
                                        (exclusions : {negativePrompt.substring(0, 50)}{negativePrompt.length > 50 ? '...' : ''})
                                    </span>
                                )}
                            </label>
                        </div>
                    )}

                    <Button
                        onClick={handleGenerate}
                        isLoading={isGenerating}
                        disabled={!prompt.trim() || isGenerating || !selectedModel}
                        leftIcon={<Wand2 size={18} />}
                        className="generate-button"
                    >
                        {isGenerating ? 'Génération en cours...' : 'Générer l\'image'}
                    </Button>
                </div>

                <div className="generator-preview glass">
                    <h2 className="preview-title">Prévisualisation</h2>

                    {/* Image area: shows loader, image, or placeholder */}
                    {isGenerating ? (
                        <div className="preview-loading">
                            <Loader2 size={48} className="animate-spin" />
                            <p>L'IA génère votre image...</p>
                            <span className="loading-hint">Cela peut prendre 10-30 secondes</span>
                        </div>
                    ) : generatedImage ? (
                        <div className="preview-result">
                            <div className="preview-image-container">
                                <img
                                    src={`data:${generatedImage.mimeType};base64,${generatedImage.data}`}
                                    alt="Image générée"
                                    className="preview-image-actual"
                                />
                            </div>

                            {/* Prompt info toggle */}
                            {usedPrompt && (
                                <div className="prompt-info-section">
                                    <button
                                        className="prompt-info-toggle"
                                        onClick={() => setShowPromptInfo(!showPromptInfo)}
                                    >
                                        {showPromptInfo ? <EyeOff size={16} /> : <Eye size={16} />}
                                        <span>{showPromptInfo ? 'Masquer le prompt envoyé' : 'Voir le prompt envoyé à l\'IA'}</span>
                                    </button>
                                    {showPromptInfo && (
                                        <div className="prompt-info-content">
                                            <div className="prompt-info-header">
                                                <strong>Prompt exact envoyé à Gemini :</strong>
                                            </div>
                                            <pre className="prompt-info-text">{usedPrompt}</pre>
                                            <p className="prompt-info-note">
                                                ℹ️ Le prompt est envoyé tel quel, sans modification ni ajout automatique.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="preview-empty">
                            <Sparkles size={64} />
                            <p>L'image générée apparaîtra ici</p>
                        </div>
                    )}

                    {/* SEO Options and Actions - visible during generation and after */}
                    {(isGenerating || generatedImage) && (
                        <>
                            <div className="seo-options">
                                <div className="seo-field">
                                    <label htmlFor="seo-filename">Nom du fichier (SEO)</label>
                                    <div className="seo-input-wrapper">
                                        <input
                                            id="seo-filename"
                                            type="text"
                                            value={seoFilename}
                                            onChange={(e) => setSeoFilename(e.target.value)}
                                            placeholder="nom-du-fichier"
                                        />
                                        <span className="seo-extension">.webp</span>
                                    </div>
                                </div>
                                <div className="seo-field">
                                    <label htmlFor="alt-text">Texte alternatif (SEO)</label>
                                    <input
                                        id="alt-text"
                                        type="text"
                                        value={altText}
                                        onChange={(e) => setAltText(e.target.value)}
                                        placeholder="Description de l'image pour l'accessibilité"
                                    />
                                </div>
                            </div>

                            <div className="preview-actions">
                                <Button
                                    variant="ghost"
                                    onClick={handleDownload}
                                    leftIcon={<Download size={18} />}
                                    disabled={isGenerating || !generatedImage}
                                >
                                    Télécharger
                                </Button>
                                <Button
                                    variant="danger"
                                    onClick={handleReject}
                                    leftIcon={<X size={18} />}
                                    disabled={isGenerating || !generatedImage}
                                >
                                    Rejeter
                                </Button>
                                <Button
                                    onClick={handleAccept}
                                    isLoading={isUploading || isLoadingSourceProduct}
                                    leftIcon={<Check size={18} />}
                                    disabled={isGenerating || !generatedImage || !selectedShop || isLoadingSourceProduct}
                                >
                                    {isLoadingSourceProduct
                                        ? 'Chargement...'
                                        : sourceProduct
                                            ? `Uploader sur "${sourceProduct.title.substring(0, 20)}${sourceProduct.title.length > 20 ? '...' : ''}"`
                                            : selectedShop
                                                ? 'Uploader sur Shopify'
                                                : 'Connectez une boutique'
                                    }
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Product Selection Modal */}
            {showProductModal && (
                <div className="modal-overlay" onClick={() => setShowProductModal(false)}>
                    <div className="product-modal glass" onClick={(e) => e.stopPropagation()}>
                        <button className="modal-close" onClick={() => setShowProductModal(false)}>
                            <X size={20} />
                        </button>

                        <h2>Sélectionner un produit</h2>
                        <p>Choisissez le produit sur lequel ajouter l'image</p>

                        <div className="product-search">
                            <Search size={18} />
                            <input
                                type="text"
                                placeholder="Rechercher un produit..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>

                        {isLoadingProducts ? (
                            <div className="products-loading">
                                <Loader2 size={32} className="animate-spin" />
                                <p>Chargement des produits...</p>
                            </div>
                        ) : filteredProducts.length === 0 ? (
                            <div className="products-empty">
                                <Package size={48} />
                                <p>Aucun produit trouvé</p>
                            </div>
                        ) : (
                            <div className="products-grid">
                                {filteredProducts.map(product => (
                                    <div
                                        key={product.id}
                                        className={`product-card ${selectedProduct?.id === product.id ? 'selected' : ''}`}
                                        onClick={() => setSelectedProduct(product)}
                                    >
                                        <div className="product-image">
                                            {product.images?.[0] ? (
                                                <img src={product.images[0].src} alt={product.title} />
                                            ) : (
                                                <Package size={32} />
                                            )}
                                        </div>
                                        <div className="product-info">
                                            <h4>{product.title}</h4>
                                            <span className="product-vendor">{product.vendor}</span>
                                        </div>
                                        {selectedProduct?.id === product.id && (
                                            <div className="product-selected-badge">
                                                <Check size={14} />
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="modal-actions">
                            <Button variant="secondary" onClick={() => setShowProductModal(false)}>
                                Annuler
                            </Button>
                            <Button
                                onClick={handleUploadToProduct}
                                isLoading={isUploading}
                                disabled={!selectedProduct}
                            >
                                Uploader l'image
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Prompt Library Modal */}
            {showLibraryModal && (
                <div className="modal-overlay" onClick={() => setShowLibraryModal(false)}>
                    <div className="prompt-library-modal glass" onClick={(e) => e.stopPropagation()}>
                        <button className="modal-close" onClick={() => setShowLibraryModal(false)}>
                            <X size={20} />
                        </button>

                        <h2><Library size={24} /> Bibliothèque de prompts</h2>
                        <p>Sélectionnez un prompt pour l'utiliser</p>

                        <div className="prompt-search">
                            <Search size={18} />
                            <input
                                type="text"
                                placeholder="Rechercher un prompt..."
                                value={promptSearchQuery}
                                onChange={(e) => setPromptSearchQuery(e.target.value)}
                            />
                        </div>

                        {isLoadingPrompts ? (
                            <div className="prompts-loading">
                                <Loader2 size={32} className="animate-spin" />
                                <p>Chargement des prompts...</p>
                            </div>
                        ) : filteredPrompts.length === 0 ? (
                            <div className="prompts-empty">
                                <Library size={48} />
                                <p>Aucun prompt trouvé</p>
                            </div>
                        ) : (
                            <div className="prompts-list">
                                {filteredPrompts.map(p => (
                                    <div
                                        key={p.id}
                                        className="prompt-item"
                                        onClick={() => handleSelectPrompt(p)}
                                    >
                                        <div className="prompt-item-header">
                                            <h4>{p.title}</h4>
                                            <span className={`prompt-type-badge ${p.type}`}>
                                                {p.type === 'public' ? 'Public' : 'Privé'}
                                            </span>
                                        </div>
                                        <p className="prompt-item-content">{p.content}</p>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Save Prompt Modal */}
            {showSaveModal && (
                <div className="modal-overlay" onClick={() => setShowSaveModal(false)}>
                    <div className="save-prompt-modal glass" onClick={(e) => e.stopPropagation()}>
                        <button className="modal-close" onClick={() => setShowSaveModal(false)}>
                            <X size={20} />
                        </button>

                        <h2><Save size={24} /> Sauvegarder le prompt</h2>

                        {saveSuccess ? (
                            <div className="save-success">
                                <Check size={48} />
                                <p>Prompt sauvegardé !</p>
                            </div>
                        ) : (
                            <>
                                <div className="save-form">
                                    <div className="form-group">
                                        <label>Titre du prompt</label>
                                        <Input
                                            value={savePromptTitle}
                                            onChange={(e) => setSavePromptTitle(e.target.value)}
                                            placeholder="Ex: Photo produit fond blanc"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Visibilité</label>
                                        <div className="visibility-options">
                                            <label className={`visibility-option ${savePromptType === 'private' ? 'selected' : ''}`}>
                                                <input
                                                    type="radio"
                                                    name="visibility"
                                                    checked={savePromptType === 'private'}
                                                    onChange={() => setSavePromptType('private')}
                                                />
                                                <span>Privé</span>
                                                <small>Visible uniquement par vous</small>
                                            </label>
                                            <label className={`visibility-option ${savePromptType === 'public' ? 'selected' : ''}`}>
                                                <input
                                                    type="radio"
                                                    name="visibility"
                                                    checked={savePromptType === 'public'}
                                                    onChange={() => setSavePromptType('public')}
                                                />
                                                <span>Public</span>
                                                <small>Visible par tous les utilisateurs</small>
                                            </label>
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label>Enrichissement du contexte</label>
                                        <div className="context-save-option">
                                            <label className="context-checkbox-label">
                                                <input
                                                    type="checkbox"
                                                    checked={useProductContext}
                                                    onChange={(e) => setUseProductContext(e.target.checked)}
                                                    className="context-checkbox"
                                                />
                                                <span className="context-checkbox-text">
                                                    Enrichir avec le contexte du produit
                                                </span>
                                            </label>
                                            <small className="context-save-hint">
                                                Inclut le titre, description, tags et images existantes du produit
                                            </small>
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label>Contenu du prompt</label>
                                        <div className="prompt-preview">
                                            {prompt}
                                        </div>
                                    </div>
                                </div>

                                <div className="modal-actions">
                                    <Button variant="secondary" onClick={() => setShowSaveModal(false)}>
                                        Annuler
                                    </Button>
                                    <Button
                                        onClick={handleSavePrompt}
                                        isLoading={isSavingPrompt}
                                        disabled={!savePromptTitle.trim()}
                                        leftIcon={<Save size={16} />}
                                    >
                                        Sauvegarder
                                    </Button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
